// "Signal or Noise" local fallback: when the worker has no round for the day
// (GET /game/today 404s, e.g. while the pipeline is paused), the daily game
// still runs. The round is built from today's cards' deep-dive key facts:
// most claims are straight from the story, a seeded few carry a doctored
// number. Deterministic per day, so every reader gets the same round and a
// replay scores the same. Scoring is local too (the server round never
// existed), with a localStorage streak so the ritual survives outages.

import { deepDiveFor } from "../deepDive";
import type { GamePick, GameResult, GameRevealItem, GameToday, InSnipCard } from "../types";

const LOCAL_STREAK_KEY = "insnip.son.localStreak";

type LocalItem = {
  id: string;
  claim: string;
  kind: "signal" | "noise";
  receipt: string;
  sourceName?: string;
  sourceUrl?: string;
  cardId: string;
};

// One built round per day, kept in module scope so scoring never needs the
// card list again (a reload rebuilds it from the same inputs before the game
// can reopen).
const roundCache = new Map<string, LocalItem[]>();

/** Deterministic PRNG (mulberry32) seeded from the day string. Shared by the
 *  other client-built dailies (front page quiz) so all of them agree per day. */
export function seededRng(seed: string): () => number {
  let h = 1779033703;
  for (let i = 0; i < seed.length; i++) {
    h = Math.imul(h ^ seed.charCodeAt(i), 3432918353);
    h = (h << 13) | (h >>> 19);
  }
  let a = h >>> 0;
  return () => {
    a |= 0; a = (a + 0x6d2b79f5) | 0;
    let t = Math.imul(a ^ (a >>> 15), 1 | a);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

/** First number run in a value that is safe to doctor (skips years, which read
 *  as dates and would produce nonsense like "5016"). */
function doctorableNumber(value: string): RegExpMatchArray | null {
  for (const m of value.matchAll(/\d[\d,]*(?:\.\d+)?/g)) {
    const n = Number(m[0].replace(/,/g, ""));
    if (n >= 1900 && n <= 2099 && Number.isInteger(n) && m[0].length === 4) continue;
    return m;
  }
  return null;
}

/** Doctor a value by shifting its leading digit to a different one (keeps the
 *  magnitude and formatting, so the lie stays plausible). */
function doctorValue(value: string, random: () => number): string | null {
  const m = doctorableNumber(value);
  if (!m || m.index === undefined) return null;
  const lead = m[0][0];
  const shift = 2 + Math.floor(random() * 6); // 2..7, never 0 or 9 (a wrap to itself)
  const doctored = String(((Number(lead) - 1 + shift) % 9) + 1);
  if (doctored === lead) return null;
  return value.slice(0, m.index) + doctored + value.slice(m.index + 1);
}

function shortHeadline(headline: string, max = 64): string {
  if (headline.length <= max) return headline;
  const cut = headline.slice(0, max);
  return cut.slice(0, Math.max(30, cut.lastIndexOf(" "))) + "…";
}

function claimText(card: InSnipCard, label: string, value: string): string {
  return `In "${shortHeadline(card.headline)}", the ${label.toLowerCase()} is ${value}.`;
}

/**
 * Build today's fallback round from the cards on the desk. Returns null when
 * there is not enough grounded material (fewer than 5 usable facts across
 * at least 4 stories) — the game then simply stays quiet, as before.
 */
export function buildLocalGame(cards: InSnipCard[], day: string): GameToday | null {
  const cached = roundCache.get(day);
  if (!cached) {
    const random = seededRng("son-" + day);
    // One fact per card, so the round spans the edition instead of quizzing one
    // story five times. Any labelled fact can carry a TRUE claim; only the
    // doctored ones need a number that is not already spelled out in the
    // headline (else the doctored copy contradicts itself on its face).
    const noisePool: { card: InSnipCard; label: string; value: string }[] = [];
    const signalPool: { card: InSnipCard; label: string; value: string }[] = [];
    for (const card of cards) {
      const facts = deepDiveFor(card)?.keyFacts ?? [];
      const doctorable = facts.find((f) => {
        if (!f.label || !f.value) return false;
        const num = doctorableNumber(f.value);
        return !!num && !card.headline.includes(num[0]);
      });
      if (doctorable) noisePool.push({ card, label: doctorable.label, value: doctorable.value });
      else {
        const usable = facts.find((f) => f.label && f.value);
        if (usable) signalPool.push({ card, label: usable.label, value: usable.value });
      }
    }
    if (noisePool.length < 2 || noisePool.length + signalPool.length < 4) return null;

    const noiseCount = Math.min(noisePool.length, 2 + (random() < 0.34 ? 1 : 0));
    // Doctored claims first (from the cards that can carry one), the rest of
    // the round filled with straight claims; extra noise-capable cards serve
    // as straight claims too.
    const picked = [
      ...noisePool.slice(0, noiseCount).map((p) => ({ ...p, doctor: true })),
      ...[...noisePool.slice(noiseCount), ...signalPool].map((p) => ({ ...p, doctor: false })),
    ].slice(0, 5);

    const items: LocalItem[] = [];
    for (let i = 0; i < picked.length; i++) {
      const { card, label, value } = picked[i];
      const doctored = picked[i].doctor ? doctorValue(value, random) : null;
      items.push(doctored
        ? {
            id: `loc-${day}-${i}`, cardId: card.id, kind: "noise",
            claim: claimText(card, label, doctored),
            receipt: `Doctored. The real ${label.toLowerCase()} is ${value}.`,
            sourceName: card.primarySourceName, sourceUrl: card.primarySourceUrl,
          }
        : {
            id: `loc-${day}-${i}`, cardId: card.id, kind: "signal",
            claim: claimText(card, label, value),
            receipt: `Straight from the story${card.primarySourceName ? `, via ${card.primarySourceName}` : ""}.`,
            sourceName: card.primarySourceName, sourceUrl: card.primarySourceUrl,
          });
    }
    // Seeded shuffle so signal/noise positions vary day to day.
    for (let i = items.length - 1; i > 0; i--) {
      const j = Math.floor(random() * (i + 1));
      [items[i], items[j]] = [items[j], items[i]];
    }
    roundCache.set(day, items);
  }
  const items = roundCache.get(day)!;
  return {
    day,
    mode: "local",
    count: items.length,
    items: items.map((it, idx) => ({ id: it.id, idx, claim: it.claim })),
    alreadyPlayed: false,
  };
}

/** Local streak: consecutive days with a played local-or-server round. */
function bumpLocalStreak(day: string): number {
  try {
    const raw = localStorage.getItem(LOCAL_STREAK_KEY);
    const prev = raw ? (JSON.parse(raw) as { day: string; streak: number }) : null;
    if (prev?.day === day) return prev.streak;
    const y = new Date(day + "T12:00:00");
    y.setDate(y.getDate() - 1);
    const yesterday = y.toLocaleDateString("en-CA");
    const streak = prev?.day === yesterday ? prev.streak + 1 : 1;
    localStorage.setItem(LOCAL_STREAK_KEY, JSON.stringify({ day, streak }));
    return streak;
  } catch {
    return 1;
  }
}

/** Score a local round. Returns null when the day's round is not in memory
 *  (never the case in a normal session — build always precedes play). */
export function scoreLocalGame(day: string, picks: GamePick[]): GameResult | null {
  const round = roundCache.get(day);
  if (!round) return null;
  const byId = new Map(picks.map((p) => [p.id, p.pick] as const));
  const items: GameRevealItem[] = round.map((it, idx) => {
    const yourPick = byId.get(it.id) ?? null;
    return {
      id: it.id, idx, kind: it.kind, receipt: it.receipt,
      sourceName: it.sourceName, sourceUrl: it.sourceUrl, cardId: it.cardId,
      claim: it.claim, yourPick, right: yourPick === it.kind,
    };
  });
  const correct = items.filter((it) => it.right).length;
  return {
    day,
    correct,
    total: items.length,
    gameStreak: bumpLocalStreak(day),
    // No population to rank against offline; a steady curve keeps the reveal
    // honest-ish without pretending to precision.
    percentile: Math.min(96, 38 + Math.round((correct / items.length) * 58)),
    shareGrid: items.map((it) => (it.right ? "🟩" : "🟥")).join(""),
    items,
  };
}
